import IconLoader from "@/features/Common/components/IconLoader";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  WellnessCategoryCardProps,
  getWellnessScoreStatusFromScore,
} from "../Common/types";
import { MetricItem } from "../Common/types";
import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { WellnessScoreAndMetricsSection } from "../Common/WellnessScoreAndMetricsSection";
import WellnessCategoryCard from "../WellnessCategoryCard";
import LifestyleFlyupModal from "./LifestyleFlyupModal";
import { useLifestyleHealthSDK } from "./Hooks";
import {
  requestImmediateSync,
  useHealthDataSync,
} from "@/hooks/useHealthDataSync";
import LifestyleSuccessScreen from "./LifestyleSuccessScreen";
import { useMachine } from "@xstate/react";
import {
  lifestyleFlowMachine,
  eventNames,
  LifestyleFlowState,
} from "./machine";
import { useNavigate, useLocation } from "react-router-dom";
import ExerciseScreen from "./Exercise";
import SleepScreen from "./Sleep";
import useHealthTrends from "../../hooks/useHealthTrends";
import {
  mapHealthTrendToSleepChartData,
  mapHealthTrendToExerciseChartData,
} from "../../utils/healthTrendsChartData";
import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import { getLifestyleMetricsFromHealthProfile } from "./utils/getLifestyleMetricsFromHealthProfile";
import { wellnessCategoryFullScreenOverlaySx } from "../Common/wellnessCategoryFullScreenOverlaySx";

function withLastSelected<T extends { isSelected?: boolean }>(data: T[]): T[] {
  if (data.length === 0) {
    return data;
  }
  return data.map((p, i) => ({
    ...p,
    isSelected: i === data.length - 1,
  }));
}

function handleMetricClick(
  metric: MetricItem,
  send: (event: { type: string }) => void
) {
  if (metric.id === "exercise") {
    send({ type: eventNames.OPEN_EXERCISE });
  } else if (metric.id === "sleep") {
    send({ type: eventNames.OPEN_SLEEP });
  }
}

export interface LifestyleCardProps {
  initialOpenExercise?: boolean;
  initialOpenSleep?: boolean;
  healthProfile?: HealthProfileSuccessResponseData | null;
  refetch?: () => void;
  domainScore?: number;
  domainCompleteness?: number;
  domainLastUpdated?: string;
}

export default function LifestyleCategory({
  initialOpenExercise = false,
  initialOpenSleep = false,
  healthProfile,
  refetch,
  domainScore,
  domainCompleteness,
  domainLastUpdated,
}: LifestyleCardProps = {}) {
  const { t } = useTranslation();
  const hasScore =
    domainScore != null && domainCompleteness != null && domainCompleteness > 0;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { requestSync } = useHealthDataSync();
  useEffect(() => {
    requestSync();
  }, [pathname, requestSync]);
  const [flowState, send] = useMachine(lifestyleFlowMachine);
  const lifestyleSectionRef = useRef<HTMLDivElement>(null);
  const [listExpanded, setListExpanded] = useState(false);
  const prevStateRef = useRef(flowState.value);

  const metricsFromProfile =
    healthProfile != null
      ? getLifestyleMetricsFromHealthProfile(healthProfile)
      : null;
  const displayMetrics = metricsFromProfile ?? [];

  const { data: sleepTrends } = useHealthTrends("Sleep");
  const { data: exerciseTrends } = useHealthTrends("IntenseExercise");
  const sleepChartData = withLastSelected(
    mapHealthTrendToSleepChartData(sleepTrends ?? [])
  );
  const exerciseChartData = withLastSelected(
    mapHealthTrendToExerciseChartData(exerciseTrends ?? [])
  );

  useEffect(() => {
    if (initialOpenExercise) {
      send({ type: eventNames.OPEN_EXERCISE });
      navigate("/home/health-score", { replace: true });
    }
  }, [initialOpenExercise, send, navigate]);

  useEffect(() => {
    if (initialOpenSleep) {
      send({ type: eventNames.OPEN_SLEEP });
      navigate("/home/health-score", { replace: true });
    }
  }, [initialOpenSleep, send, navigate]);

  const {
    handleConnect,
    isConnected,
    hasAnyPermissions,
    isAvailable,
    resolvedIsAndroid,
    resolvedIsIOS,
  } = useLifestyleHealthSDK();

  const isNativeMobile = resolvedIsAndroid || resolvedIsIOS;

  const hasTriggeredPermissionsRef = React.useRef(false);

  useEffect(() => {
    if (flowState.matches(LifestyleFlowState.AwaitingPermissions)) {
      hasTriggeredPermissionsRef.current = false;
    }
  }, [flowState]);

  useEffect(() => {
    if (
      flowState.matches(LifestyleFlowState.AwaitingPermissions) &&
      (isConnected || hasAnyPermissions) &&
      !hasTriggeredPermissionsRef.current
    ) {
      hasTriggeredPermissionsRef.current = true;
      send({ type: eventNames.PERMISSIONS_GRANTED });
      requestImmediateSync();
    }
  }, [flowState, isConnected, hasAnyPermissions, send]);

  // flowState.value is intentional — restarts the timer on any state change
  useEffect(() => {
    if (!flowState.matches(LifestyleFlowState.AwaitingPermissions)) {
      return;
    }
    const timer = setTimeout(() => {
      send({ type: eventNames.PERMISSIONS_DENIED });
    }, 30_000);
    return () => clearTimeout(timer);
  }, [flowState.value, send]);

  useEffect(() => {
    const wasExercise = prevStateRef.current === LifestyleFlowState.Exercise;
    const wasSleep = prevStateRef.current === LifestyleFlowState.Sleep;
    if (
      (wasExercise || wasSleep) &&
      flowState.matches(LifestyleFlowState.Idle)
    ) {
      setListExpanded(true);
      requestAnimationFrame(() => {
        const el = lifestyleSectionRef.current;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevStateRef.current = flowState.value;
  }, [flowState.value]);

  function handleSecondaryButtonClick() {
    if (resolvedIsIOS) {
      send({ type: eventNames.CONNECT_CLICKED });
      try {
        handleConnect();
      } catch (err) {
        console.error(
          "iOS HealthKit permission request failed",
          err instanceof Error ? err : new Error(String(err)),
          {
            phase: "lifestyleIOSConnect",
            reason: "request_permissions_error",
          }
        );
        send({ type: eventNames.PERMISSIONS_DENIED });
      }
    } else {
      send({ type: eventNames.OPEN_FLYUP });
    }
  }

  const resolvedStatus =
    domainScore != null || domainCompleteness != null
      ? getWellnessScoreStatusFromScore(domainScore ?? 0, domainCompleteness)
      : undefined;

  const lifestyleProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader icon="LifestyleIcon" color="primary" sx={{ fontSize: 40 }} />
    ),
    title: t("Lifestyle.screenTitle"),
    status: resolvedStatus,
    description: t(
      hasScore ? "Lifestyle.description.update" : "Lifestyle.description"
    ),
    lastUpdated: domainLastUpdated,
  };

  if (flowState.matches(LifestyleFlowState.Exercise)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <ExerciseScreen
          chartData={exerciseChartData}
          status={getWellnessScoreStatusFromScore(
            parseFloat(
              displayMetrics?.find((m) => m.id === "exercise")?.value ?? "0"
            )
          )}
          lastUpdated={
            displayMetrics?.find((m) => m.id === "exercise")?.lastUpdated ??
            undefined
          }
          onBack={() => send({ type: eventNames.BACK_FROM_EXERCISE })}
          onOpenLifestyle={() =>
            navigate("/home/health-score/lifestyle-questionnaire", {
              state: { from: "exercise" },
            })
          }
        />
      </Box>
    );
  }

  if (flowState.matches(LifestyleFlowState.Sleep)) {
    return (
      <Box sx={wellnessCategoryFullScreenOverlaySx}>
        <SleepScreen
          chartData={sleepChartData}
          status={getWellnessScoreStatusFromScore(
            parseFloat(
              displayMetrics?.find((m) => m.id === "sleep")?.value ?? "0"
            )
          )}
          lastUpdated={
            displayMetrics?.find((m) => m.id === "sleep")?.lastUpdated ??
            undefined
          }
          onBack={() => send({ type: eventNames.BACK_FROM_SLEEP })}
          onOpenLifestyle={() =>
            navigate("/home/health-score/lifestyle-questionnaire", {
              state: { from: "sleep" },
            })
          }
        />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box ref={lifestyleSectionRef}>
        <WellnessCategoryCard {...lifestyleProps}>
          <WellnessScoreAndMetricsSection
            show={hasScore}
            healthScore={domainScore ?? 0}
            metrics={displayMetrics}
            onMetricClick={(metric) => handleMetricClick(metric, send)}
            expanded={listExpanded}
            onExpandedChange={setListExpanded}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              navigate("/home/health-score/lifestyle-questionnaire")
            }
            fullWidth
          >
            {hasScore
              ? t("Lifestyle.buttonLabel.primary.update")
              : t("Lifestyle.buttonLabel.primary")}
          </Button>
          {!(isConnected || hasAnyPermissions) &&
            (isAvailable || isNativeMobile) && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSecondaryButtonClick}
                fullWidth
              >
                {t("Lifestyle.buttonLabel.secondary")}
              </Button>
            )}
        </WellnessCategoryCard>
      </Box>

      <LifestyleFlyupModal
        isButtonDisabled={!isAvailable && resolvedIsAndroid}
        isOpen={flowState.matches(LifestyleFlowState.FlyupModal)}
        onClose={() => send({ type: eventNames.CLOSE_FLYUP })}
        onConnect={() => {
          handleConnect();
          send({ type: eventNames.CONNECT_CLICKED });
        }}
        showAndroidSection={resolvedIsAndroid}
      />

      <LifestyleSuccessScreen
        isOpen={
          flowState.matches(LifestyleFlowState.Success) &&
          (isConnected || hasAnyPermissions)
        }
        onClose={() => {
          send({ type: eventNames.CLOSE_SUCCESS });
          refetch?.();
        }}
        onContinue={() => {
          send({ type: eventNames.CONTINUE_TO_DASHBOARD });
          refetch?.();
        }}
      />
    </React.Fragment>
  );
}
